<template>
    <section class="collections-list-slice">
        <div class="content">
            <PrismicRichText :field="slice.primary.title" class="title" />
            <template v-if="!currentBreakpoints?.isMobile">
                <div v-if="!currentBreakpoints?.isMobile" class="collections-grid">
                    <CollectionThumbnail v-for="collection in collections" :key="collection.id" :collection="collection" />
                </div>
            </template>
            <template v-else>
                <Swiper
                    slides-per-view="auto"
                    :space-between="24"
                    :breakpoints="{[breakpoints.mobile]: {slidesPerView: 'auto', spaceBetween: 40}}"
                    :modules="MODULES"
                    :navigation="true"
                >
                    <SwiperSlide v-for="collection in collections" :key="collection.id" class="slider-card">
                        <CollectionThumbnail :collection="collection" />
                    </SwiperSlide>
                </Swiper>
            </template>
        </div>
        <div class="content">
            <div class="button-row">
                <NuxtLink v-slot="{href, navigate}" :to="$withLocalePrefix('/resources/collections')" custom>
                    <VJoyButton variant="main" :href="href" @click="navigate">{{ t('resources.collections.seeAll') }}</VJoyButton>
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {inject} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {CollectionDocument, CollectionDocumentData, CollectionsListSlice} from '@prismicio-types';
    // @ts-expect-error - swiper has no types
    import {Navigation} from 'swiper';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import {useRuntimeConfig, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import {MEDIA_BREAKPOINTS} from '~/app/keys';
    import CollectionThumbnail from '~/components/CollectionThumbnail.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';

    const MODULES = [Navigation];
    const props = defineProps({
        ...getSliceComponentProps<CollectionsListSlice>(),
    });
    const currentBreakpoints = inject(MEDIA_BREAKPOINTS);
    const {breakpoints} = useRuntimeConfig().public;
    const {t} = useTranslation();
    const collections = [
        props.slice.primary.collection1,
        props.slice.primary.collection2,
        props.slice.primary.collection3,
        props.slice.primary.collection4,
    ].filter((article) => isFilled.contentRelationship<'collection', string, CollectionDocumentData>(article)) as (
        FilledContentRelationshipField<'collection', string, CollectionDocumentData> | CollectionDocument
    )[];

    const searchQueries = props.slice.items.map(() =>
        usePrismicSearch({
            type: 'collection',
            pageSize: 3 - collections.length,
            excludes: collections.map((collection) => collection.id),
        }),
    );
    if (searchQueries.length === 0) {
        searchQueries.push(
            usePrismicSearch({
                type: 'collection',
                pageSize: 4 - collections.length,
                excludes: collections.map((collection) => collection.id),
            }),
        );
    }

    const complementaryCollections = await Promise.all(searchQueries);
    const mostRecentComplementaryCollections = complementaryCollections
        .map((query) => query?.results)
        .flat()
        .filter((collection) => collection !== undefined)
        .sort((a, b) => new Date(b?.data?.releaseDate).getTime() - new Date(a?.data?.releaseDate).getTime())
        .slice(0, 4 - collections.length) as CollectionDocument[];
    collections.push(...mostRecentComplementaryCollections);
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .collections-list-slice {
        overflow: hidden;
        position: relative;

        .content {
            @include content-width;

            .title {
                margin-bottom: var(--joy-core-spacing-9);
                @media (max-width: $mobile-breakpoint) {
                    margin-bottom: var(--joy-core-spacing-7);
                }
            }

            .collections-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-gap: var(--joy-core-spacing-9);
                justify-items: center;
                @media (max-width: $tablet-breakpoint) {
                    grid-template-columns: repeat(2, 1fr);
                    grid-gap: var(--joy-core-spacing-6);
                }
                @media (max-width: $mobile-breakpoint) {
                    grid-template-columns: repeat(1, 1fr);
                }
            }

            .swiper {
                overflow: visible;
                margin-right: 40px;
                padding-bottom: 80px;
                @media (max-width: $mobile-breakpoint) {
                    margin-right: 0;
                }
            }

            .swiper-wrapper {
                overflow: visible;
            }

            .swiper-slide {
                width: 290px;
            }

            .swiper-button-prev,
            .swiper-button-next {
                content: url('~/assets/svg/arrowRight.svg');
                display: block;
                padding: var(--joy-core-spacing-3);
                border-radius: var(--joy-core-radius-6);
                height: 42px;
                width: 42px;
                background-color: var(--joy-color-primary-50);
                position: absolute;
                top: unset;
                bottom: 10px;
                left: unset;
                right: 0;
            }

            .swiper-button-prev {
                transform: rotate(180deg);
                left: unset;
                right: 60px;
            }
        }

        .button-row {
            display: flex;
            justify-content: center;
            margin-top: 80px;
            @media (max-width: $mobile-breakpoint) {
                margin-top: var(--joy-core-spacing-3);
            }
        }
    }
</style>
